import React, { useState, useEffect } from 'react';
import TablePagination from "@mui/material/TablePagination";
import { Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TableSortLabel, Skeleton } from "@mui/material";
import { Methods } from '../../core/utility';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
export interface SortingColumnsType {
  name: string | "element key Ex. instagram_handle_ig",
  columnSize: string | "columnSize Ex. 120px",
};
const methods = new Methods();
export interface ColumnDisplayNames {
  [key: string]: string; //{instagram_handle_ig: Instagram Handle}
};
interface Props {
  pagination?: boolean;
  filter?: string;
  orderByColumnName: string;
  columnDisplayName?: ColumnDisplayNames;
  sortingColumnType?: SortingColumnsType[];
  inputDataSource: any[];
  setRenderData: (data: any[]) => void;
  renderData: any[];
  children?: any
  tableMinWidth?: number;
  className?: string;
  loader?: boolean;
  noData?: boolean;
  selectedMainTab?:string;
};
export const TableComponentBrandPulse: React.FC<Props> = ({
  pagination = false,
  orderByColumnName,
  columnDisplayName,
  sortingColumnType,
  inputDataSource,
  children,
  tableMinWidth = 650,
  className,
  loader,
  noData,
  selectedMainTab
}: Props) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState(orderByColumnName);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [paginatedData, setPaginatedData] = useState<any[]>([]);

  const sortedData = React.useMemo(() => {
    return [...inputDataSource].sort((a, b) => {
      if (a[orderBy] < b[orderBy]) return order === "asc" ? -1 : 1;
      if (a[orderBy] > b[orderBy]) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [inputDataSource, orderBy, order]);

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    setPaginatedData(pagination ? sortedData.slice(start, end) : sortedData);
  }, [sortedData, page, rowsPerPage, pagination]);

  return (
    <div className={`table-component ${className}`}>
      <TableContainer className="common-table">
        <Table sx={{ minWidth: tableMinWidth }} aria-label="custom pagination table">
          <TableHead className="common-table-header">
            <TableRow>
              {sortingColumnType?.map((column) => (
                <TableCell
                  className="table_cell"
                  style={{ minWidth: column.columnSize }}
                  align="left"
                  key={column.name}
                >
                  <TableSortLabel
                    active={orderBy === column.name}
                    direction={order}
                    onClick={() => handleSort(column.name)}
                  >
                    {(columnDisplayName && columnDisplayName[column.name]) || column.name}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody className="common-table-body">
            {loader ? (
              // Show some skeleton rows while loading
              Array.from(new Array(rowsPerPage)).map((_, idx) => (
                <TableRow key={`skeleton-${idx}`}>
                  {sortingColumnType?.map((column) => (
                    <TableCell key={column.name}>
                      <Skeleton width="auto" height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (!paginatedData || paginatedData.length === 0 || noData) ? (
              <TableRow>
                <TableCell colSpan={sortingColumnType?.length || 1} className="text-center">
                  No data found
                </TableCell>
              </TableRow>
            ) : paginatedData.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {sortingColumnType?.map((column) => (
                  <TableCell key={column.name}>
                    {
                      loader ? (
                        <Skeleton width={"auto"} height={20} />
                      ) : column.name === "Name" || column.name === "user_posted" ? (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <a target='_blank' rel="noopener noreferrer" href={`${selectedMainTab === 'influencer' ? `https://www.instagram.com/${row?.user_posted}` : `https://www.instagram.com/p/${row?.shortcode}`}`}>
                            <img
                              src={selectedMainTab === 'influencer' ? row?.gcp_profile_image_link : row?.gcp_display_url}
                              alt="Profile"
                              style={{
                                width: '45px',
                                height: '45px',
                                borderRadius: '50%',
                                marginRight: '10px',
                                border: '1px solid rgba(0, 0, 0, 0.4)'
                              }}
                            />
                          </a>
                          <div className="d-flex flex-column">
                            <strong><a className='profile-link-name mb-1' target='_blank' rel="noopener noreferrer" href={`${selectedMainTab === 'influencer' ? `https://www.instagram.com/${row?.user_posted}` : `https://www.instagram.com/p/${row?.shortcode}`}`}>{row?.full_name}</a></strong>
                            <a className='profile-link' target='_blank' href={`https://www.instagram.com/${row?.user_posted}`}>
                            {row?.user_posted}
                            </a>
                          </div>
                        </div>
                      ) : [ "avg_followers","followers"].includes(column.name) ? (
                        methods.convertExactNumber(row[column.name])
                      )
                        : [ "likes", "comments", "video_plays", "amount", "emv_total"].includes(column.name) ? (
                        methods.convertExactNumber(row[column.name])
                      ):
                      ["er"].includes(column.name) ? (
                        <>
                          {row[column.name] > 1
                            ? `${row[column.name].toFixed(2)}%` // show as is
                            : `${row[column.name].toFixed(2)}%` // show with 1 decimal and "%"
                          }
                        </>
                      ) : ["date_posted"].includes(column.name) ? (
                          dayjs(row[column.name]).format("MMM D, YYYY")
                        )
                          : (
                            row[column.name] ?? "-"
                          )
                    }
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && (
        <TablePagination
          className="common-pagination"
          component="div"
          rowsPerPageOptions={[5, 10, 25]}
          count={sortedData.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ color: "#00aae5", marginTop: "10px" }}
        />
      )}
    </div>
  );
};
